/**
 * Scope-update PUT payload builder.
 *
 * Extracted from ProjectDetailPage so the mapping is unit-testable in isolation
 * (and so the page file stays component-only for react-refresh).
 *
 * GitHub #174: the project page's client-scope card editor saved via
 * PUT /api/projects/{id}/scopes/{scopeId} with a body that dropped
 * `establishment_id` entirely, so the establishment/DBA picker silently never
 * persisted (200 OK, no change). This builder is the single source of truth for
 * that request body.
 */

/**
 * Input shape from ScopeCard.handleSave's onUpdate callback.
 * Mirrors the editable fields the scope editor surfaces.
 */
export interface ScopeUpdateInput {
  id: string;
  organization_id?: string;
  scope_type_id?: string;
  contact_id?: string | null;
  status?: string;
  status_notes?: string;
  // Phase 9.28 / v0.12.22: project-default establishment (DBA) override.
  //   undefined => non-client scope, field omitted from PUT (don't clobber).
  //   null      => user picked "None (use organization)" — explicit clear.
  //   number    => a specific establishment selected.
  establishment_id?: number | null;
}

/** Wire shape passed to api.projectScopes.update (keys with `undefined` are dropped there). */
export interface ScopeUpdatePayload {
  organization_id?: string;
  scope_type_id?: string;
  contact_id?: string | null;
  status?: string;
  status_notes?: string;
  establishment_id?: number | null;
}

/**
 * Builds the PUT /api/projects/{id}/scopes/{scopeId} request payload from the
 * scope editor's output.
 *
 * GitHub #174 fix: `establishment_id` is forwarded verbatim — including explicit
 * `null` (user cleared) vs `undefined` (non-client scope, omitted). The
 * downstream api.projectScopes.update only serializes keys that are
 * `!== undefined`, so the undefined-vs-null distinction is preserved on the wire.
 *
 * NOTE: scope_type_id is read-only after creation (Phase 7.5.15); ScopeCard does
 * not emit it, so it stays `undefined` here and is omitted by the API layer.
 */
export function buildScopeUpdatePayload(data: ScopeUpdateInput): ScopeUpdatePayload {
  return {
    organization_id: data.organization_id,
    scope_type_id: data.scope_type_id,
    contact_id: data.contact_id || null,
    status: data.status,
    status_notes: data.status_notes,
    // #174 fix: forward establishment_id (undefined stays omitted by the API layer)
    establishment_id: data.establishment_id,
  };
}
